import { useState, useRef, useSyncExternalStore } from 'react'
import { useAutoRetry } from '../../hooks/useAutoRetry'
import ErrorRetryBar from '../../components/ErrorRetryBar'
import { configService, CONFIG_KEYS } from '../../services/configService'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Avatar,
  Button,
  Input,
  Text,
  Spinner,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  Tooltip,
  Checkbox,
  Select,
  MessageBar,
  MessageBarBody,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  Add24Regular,
  Search24Regular,
  LinkRegular,
  LinkDismissRegular,
  FolderLinkRegular,
  ArrowDownload24Regular,
  ArrowUpload24Regular,
  CheckmarkCircle24Regular,
  DismissCircle24Regular,
  Warning24Regular,
  ChevronDownRegular,
  EditRegular,
} from '@fluentui/react-icons'
import { employeesService, EMPLOYEE_STATUS, EMPLOYMENT_TYPE } from '../../services/employeesService'
import { exportsService } from '../../services/exportsService'
import { importsService, ImportResult } from '../../services/importsService'
import { useAuth } from '../../auth/useAuth'

function useIsMobile(breakpoint = 640) {
  return useSyncExternalStore(
    cb => { window.addEventListener('resize', cb); return () => window.removeEventListener('resize', cb) },
    () => window.innerWidth <= breakpoint,
    () => false,
  )
}

const useStyles = makeStyles({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    gap: '8px',
    flexWrap: 'wrap',
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  toolbar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  searchInput: {
    width: '300px',
    '@media (max-width: 479px)': {
      width: '100%',
    },
  },
  row: { cursor: 'pointer', ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover } },
  // Wrapper pro horizontální posuvník
  tableWrapper: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch' as any,
    marginLeft: '-4px',   // kompenzace vnitřního paddingu tabulky
    paddingBottom: '4px', // prostor pro scrollbar
  },
  // Lepkavé záhlaví sloupců — zůstane viditelné při scrollování řádků
  stickyHead: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    '& > *': { backgroundColor: tokens.colorNeutralBackground1 },   // krytí i na buňkách
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  nameLines: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1px',
  },
  nameSub: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  contactCell: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1px',
  },
  emailLink: {
    fontSize: '13px',
    color: tokens.colorBrandForeground1,
    textDecoration: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    display: 'block',
    ':hover': { textDecoration: 'underline' },
  },
  phoneText: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  contractCell: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1px',
  },
  contractType: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
  },
  contractDate: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  // Uppercase hlavičky
  th: {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: tokens.colorNeutralForeground3,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  importRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '6px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  importIcon: {
    flexShrink: 0,
    marginTop: '2px',
  },
  importMessages: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    fontSize: '12px',
  },
})

function getSpFallback(emp: any, configData: any[]): string {
  const get = (key: string) => configData?.find((c: any) => c.key === key)?.value ?? ''
  if (emp.status === 3) return get(CONFIG_KEYS.SharePointUrl_EmployeeTerminated) // Terminated
  return get(CONFIG_KEYS.SharePointUrl_EmployeeActive)
}

export default function EmployeesPage() {
  const styles = useStyles()
  const isMobile = useIsMobile()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isHR, isAdmin, isAuthenticated } = useAuth()
  const [search, setSearch] = useState('')
  const [managerSearch, setManagerSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showTerminated, setShowTerminated] = useState(false)
  const now = new Date()
  // Výchozí export = předchozí měsíc
  const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth()
  const prevYear  = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
  const [exportMonth, setExportMonth] = useState(prevMonth)
  const [exportYear, setExportYear]   = useState(prevYear)
  const [isExporting, setIsExporting] = useState(false)
  const [isExportingData, setIsExportingData] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)


  const handleExport = async () => {
    setIsExporting(true)
    try { await exportsService.downloadAttendance(exportYear, exportMonth) }
    finally { setIsExporting(false) }
  }

  const handleExportData = async () => {
    setIsExportingData(true)
    try { await exportsService.downloadEmployees() }
    finally { setIsExportingData(false) }
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    // Reset file input so same file can be re-selected
    e.target.value = ''
    setIsImporting(true)
    setImportError(null)
    setImportResult(null)
    try {
      const result = await importsService.importEmployees(file)
      setImportResult(result)
      // Refresh employee list
      queryClient.invalidateQueries({ queryKey: ['employees'] })
    } catch (err: any) {
      setImportError(err?.response?.data?.detail ?? err?.message ?? 'Chyba při importu.')
    } finally {
      setIsImporting(false)
    }
  }

  const { data: configData } = useQuery({
    queryKey: ['config'],
    queryFn: configService.getAll,
    staleTime: 5 * 60_000,
  })
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['employees', search, managerSearch, page, showTerminated],
    queryFn: () => employeesService.getAll({
      search: search || undefined,
      managerSearch: managerSearch || undefined,
      page,
      pageSize: 100,
      excludeTerminated: !showTerminated,
    }),
    enabled: isAuthenticated,
    retry: 2,
    staleTime: 30_000,
  })

  const { autoRetryCount, autoRetryCountdown, maxRetries, resetAndRetry } = useAutoRetry(isError, refetch)

  // #23: globální SharePoint složka (aktivní zaměstnanci) — není per uživatel, patří do toolbaru
  const spMainUrl = getSpFallback({ status: 1 } as any, configData ?? [])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div className={styles.header}>
        <Text size={600} weight="semibold">{t('employees.title')}</Text>
        <div className={styles.headerActions}>
          {spMainUrl && (
            <Tooltip content="Otevřít SharePoint složku" relationship="label">
              <Button appearance="subtle" icon={<FolderLinkRegular />}
                onClick={() => window.open(spMainUrl, '_blank', 'noopener')}>
                SharePoint
              </Button>
            </Tooltip>
          )}
          {(isHR || isAdmin) && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={handleFileSelected}
              />
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button icon={<ChevronDownRegular />} iconPosition="after">
                    Export &amp; Import
                  </Button>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem
                      icon={<ArrowDownload24Regular />}
                      onClick={() => setAttendanceDialogOpen(true)}
                    >
                      Export docházky…
                    </MenuItem>
                    <MenuDivider />
                    <MenuItem
                      icon={isExportingData ? <Spinner size="tiny" /> : <ArrowDownload24Regular />}
                      onClick={handleExportData}
                      disabled={isExportingData}
                    >
                      Export zaměstnanců (Excel)
                    </MenuItem>
                    <MenuItem
                      icon={isImporting ? <Spinner size="tiny" /> : <ArrowUpload24Regular />}
                      onClick={handleImportClick}
                      disabled={isImporting}
                    >
                      Import zaměstnanců
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            </>
          )}
          {(isHR || isAdmin) && (
            <Button appearance="primary" icon={<Add24Regular />} onClick={() => navigate('/employees/new')}>
              {t('employees.add')}
            </Button>
          )}
        </div>
      </div>

      <div className={styles.toolbar}>
        <Input
          className={styles.searchInput}
          contentBefore={<Search24Regular />}
          placeholder={t('employees.search')}
          value={search}
          onChange={(_, d) => { setSearch(d.value); setPage(1) }}
        />
        <Input
          className={styles.searchInput}
          contentBefore={<Search24Regular />}
          placeholder="Hledat dle manažera..."
          value={managerSearch}
          onChange={(_, d) => { setManagerSearch(d.value); setPage(1) }}
        />
        <Checkbox
          label="Zobrazit ukončené"
          checked={showTerminated}
          onChange={(_, d) => { setShowTerminated(!!d.checked); setPage(1) }}
        />
        <div style={{ flex: 1 }} />
        {data && (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            Celkem: {data.totalCount}
          </Text>
        )}
      </div>

      <ErrorRetryBar
        isError={isError}
        message="Nepodařilo se načíst zaměstnance."
        autoRetryCount={autoRetryCount}
        autoRetryCountdown={autoRetryCountdown}
        maxRetries={maxRetries}
        onRetry={resetAndRetry}
      />

      {importError && (
        <MessageBar intent="error" style={{ marginBottom: 12 }}>
          <MessageBarBody>{importError}</MessageBarBody>
        </MessageBar>
      )}

      {/* Dialog s výsledky importu */}
      <Dialog open={importResult !== null} onOpenChange={() => setImportResult(null)}>
        <DialogSurface style={{ maxWidth: 640 }}>
          <DialogTitle>Výsledek importu</DialogTitle>
          <DialogBody>
            <DialogContent>
              {importResult && (
                <>
                  <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
                    <Text><CheckmarkCircle24Regular style={{ color: tokens.colorPaletteGreenForeground1, verticalAlign: 'middle' }} /> Vytvořeno: <strong>{importResult.created}</strong></Text>
                    <Text><CheckmarkCircle24Regular style={{ color: tokens.colorBrandForeground1, verticalAlign: 'middle' }} /> Aktualizováno: <strong>{importResult.updated}</strong></Text>
                    <Text><DismissCircle24Regular style={{ color: tokens.colorPaletteRedForeground1, verticalAlign: 'middle' }} /> Přeskočeno: <strong>{importResult.skipped}</strong></Text>
                  </div>

                  {/* Nově vytvořené záznamy */}
                  {importResult.rows.filter(r => r.action === 'created').length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <Text size={200} weight="semibold" style={{ color: tokens.colorPaletteGreenForeground1, display: 'block', marginBottom: 6 }}>
                        ✅ Nově vytvořeni ({importResult.rows.filter(r => r.action === 'created').length})
                      </Text>
                      <div style={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {importResult.rows
                          .filter(r => r.action === 'created')
                          .map(row => (
                            <div key={row.rowNumber} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                              <Text size={100} style={{ color: tokens.colorNeutralForeground3, minWidth: 52 }}>řádek {row.rowNumber}</Text>
                              <Text size={100} weight="semibold">{row.name}</Text>
                              {row.messages.filter(m => m.type === 'warning').map((msg, i) => (
                                <Text key={i} size={100} style={{ color: tokens.colorPaletteYellowForeground1 }}>⚠️ {msg.text}</Text>
                              ))}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Chyby a přeskočené */}
                  {importResult.rows.filter(r => r.messages.length > 0 || r.action === 'skipped').length > 0 && (
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                      {importResult.rows
                        .filter(r => r.messages.some(m => m.type === 'error') || r.action === 'skipped')
                        .map(row => (
                          <div key={row.rowNumber} className={styles.importRow}>
                            <div className={styles.importIcon}>
                              {row.action === 'skipped'
                                ? <DismissCircle24Regular style={{ color: tokens.colorPaletteRedForeground1 }} />
                                : <Warning24Regular style={{ color: tokens.colorPaletteYellowForeground1 }} />
                              }
                            </div>
                            <div>
                              <Text weight="semibold" size={200}>Řádek {row.rowNumber} — {row.name}</Text>
                              <div className={styles.importMessages}>
                                {row.messages.map((msg, i) => (
                                  <Text key={i} size={100} style={{
                                    color: msg.type === 'error'
                                      ? tokens.colorPaletteRedForeground1
                                      : tokens.colorPaletteYellowForeground1,
                                  }}>
                                    {msg.type === 'error' ? '❌' : '⚠️'} {msg.text}
                                  </Text>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  {importResult.rows.length === 0 && (
                    <Text>Všechny záznamy byly úspěšně zpracovány bez upozornění.</Text>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setImportResult(null)}>Zavřít</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Dialog: export docházky */}
      <Dialog open={attendanceDialogOpen} onOpenChange={(_, s) => setAttendanceDialogOpen(s.open)}>
        <DialogSurface style={{ maxWidth: 360 }}>
          <DialogTitle>Export docházky</DialogTitle>
          <DialogBody>
            <DialogContent>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <Select
                  value={String(exportMonth)}
                  onChange={(_, d) => setExportMonth(Number(d.value))}
                  style={{ flex: 1 }}
                >
                  {['Leden','Únor','Březen','Duben','Květen','Červen',
                    'Červenec','Srpen','Září','Říjen','Listopad','Prosinec']
                    .map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </Select>
                <Select
                  value={String(exportYear)}
                  onChange={(_, d) => setExportYear(Number(d.value))}
                  style={{ width: 96 }}
                >
                  {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1]
                    .map(y => <option key={y} value={y}>{y}</option>)}
                </Select>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setAttendanceDialogOpen(false)}>
                Zrušit
              </Button>
              <Button
                appearance="primary"
                icon={isExporting ? <Spinner size="tiny" /> : <ArrowDownload24Regular />}
                disabled={isExporting}
                onClick={async () => {
                  await handleExport()
                  setAttendanceDialogOpen(false)
                }}
              >
                Stáhnout
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {isLoading ? (
        <Spinner label={t('app.loading')} />
      ) : (
        <div className={styles.tableWrapper} style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <Table style={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHeader>
              <TableRow className={styles.stickyHead}>
                <TableHeaderCell style={{ width: isMobile ? '55%' : '26%' }}><span className={styles.th}>Zaměstnanec</span></TableHeaderCell>
                {!isMobile && <TableHeaderCell style={{ width: '22%' }}><span className={styles.th}>E-mail / Mobil</span></TableHeaderCell>}
                <TableHeaderCell style={{ width: isMobile ? '30%' : '16%' }}><span className={styles.th}>Nadřízený / Tým</span></TableHeaderCell>
                {!isMobile && <TableHeaderCell style={{ width: 150 }}><span className={styles.th}>Typ (úvazek) / Nástup</span></TableHeaderCell>}
                <TableHeaderCell style={{ width: isMobile ? '15%' : undefined }}><span className={styles.th}>Stav</span></TableHeaderCell>
                {(isHR || isAdmin) && !isMobile && <TableHeaderCell style={{ width: 72 }} />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.items.map(emp => {
                const empType = EMPLOYMENT_TYPE[emp.employmentType]
                // #23: úvazek v hodinách za týden (ne %); plný úvazek (40 h) se neuvádí
                const fmtH = (h: number) => (h % 1 === 0 ? String(h) : h.toFixed(1).replace('.', ','))
                const empTypeLabel = empType
                  ? (emp.weeklyHours === 40 ? empType.label : `${empType.label} (${fmtH(emp.weeklyHours)} h)`)
                  : '–'
                const hireDateFormatted = emp.hireDate
                  ? new Date(emp.hireDate).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' })
                  : null
                return (
                <TableRow key={emp.id} className={styles.row} onClick={() => navigate(`/employees/${emp.id}`)}>
                  {/* Avatar + jméno (2 řádky: jméno / pozice) */}
                  <TableCell>
                    <div className={styles.nameCell}>
                      <Avatar size={32} name={emp.fullName} />
                      <div className={styles.nameLines}>
                        <Text weight="semibold" size={300}>{emp.fullName}</Text>
                        {emp.positionName && (
                          <span className={styles.nameSub}>{emp.positionName}</span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  {/* E-mail (mailto) + telefon */}
                  {!isMobile && (
                    <TableCell>
                      <div className={styles.contactCell} onClick={e => e.stopPropagation()}>
                        {emp.email && (
                          <a href={`mailto:${emp.email}`} className={styles.emailLink}>
                            {emp.email}
                          </a>
                        )}
                        {emp.phone && (
                          <span className={styles.phoneText}>{emp.phone}</span>
                        )}
                      </div>
                    </TableCell>
                  )}
                  {/* Nadřízený / Tým */}
                  <TableCell>
                    <div className={styles.nameLines}>
                      <Text size={300}>{emp.managerFullName ?? '—'}</Text>
                      {emp.departmentName && (
                        <span className={styles.nameSub}>{emp.departmentName}</span>
                      )}
                    </div>
                  </TableCell>
                  {/* Typ (úvazek) / datum nástupu */}
                  {!isMobile && (
                    <TableCell>
                      <div className={styles.contractCell}>
                        <span className={styles.contractType}>{empTypeLabel}</span>
                        {hireDateFormatted && (
                          <span style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>{hireDateFormatted}</span>
                        )}
                      </div>
                    </TableCell>
                  )}
                  {/* Stav — úzký badge */}
                  <TableCell>
                    <Badge
                      appearance="filled"
                      color={EMPLOYEE_STATUS[emp.status]?.color ?? 'subtle'}
                    >
                      {(EMPLOYEE_STATUS[emp.status]?.label ?? String(emp.status)).replace('Rodičovská dovolená', 'Rodičovská')}
                    </Badge>
                  </TableCell>
                  {(isHR || isAdmin) && !isMobile && (
                    <TableCell onClick={e => e.stopPropagation()} style={{ textAlign: 'right' }}>
                      {emp.vacationReviewNeeded && (
                        <Tooltip content="Změnil se úvazek — přepočítej nárok dovolené (Korekce)" relationship="label">
                          <span style={{ verticalAlign: 'middle', marginRight: 4 }}>
                            <Warning24Regular style={{ color: tokens.colorStatusWarningForeground1, fontSize: 16 }} />
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip
                        content={emp.hasEntraId ? 'Napojeno na Entra ID' : 'Není napojeno na Entra ID — doplňte'}
                        relationship="label"
                      >
                        <span style={{ verticalAlign: 'middle', marginRight: 4 }}>
                          {emp.hasEntraId
                            ? <LinkRegular style={{ color: tokens.colorPaletteGreenForeground1, fontSize: 16 }} />
                            : <LinkDismissRegular style={{ color: tokens.colorPaletteRedForeground1, fontSize: 16 }} />
                          }
                        </span>
                      </Tooltip>
                      <Button
                        size="small" appearance="subtle" icon={<EditRegular />}
                        onClick={() => navigate(`/employees/${emp.id}/edit`)}
                      />
                    </TableCell>
                  )}
                </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {data && (
        <div className={styles.pagination}>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            Celkem: {data.totalCount} {showTerminated ? '' : '(bez ukončených)'}
          </Text>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</Button>
            <Text style={{ alignSelf: 'center' }}>{page} / {Math.ceil(data.totalCount / 100) || 1}</Text>
            <Button disabled={page * 100 >= data.totalCount} onClick={() => setPage(p => p + 1)}>›</Button>
          </div>
        </div>
      )}
    </div>
  )
}
